import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useLocation } from "react-router-dom";
import { HelmetDexteel } from "../../../helmet/HelmetDexteel";
import { TableOperators } from "./components/TableOperators";
import type { Operator } from "./models/Operator";
import { getOperators } from "./repositories/OperatorsRepository";

export const operatorUrl = "/configuration/operators/";
export const OPERATOR_MODAL = {
	NONE: "",
	CREATE: "create",
	UPDATE: "update",
	DELETE: "delete",
} as const;

export type OperatorModal =
	(typeof OPERATOR_MODAL)[keyof typeof OPERATOR_MODAL];

type OperatorRouteState = {
	modal: OperatorModal;
	operatorId?: string;
};

export const getContextAssetId = (defaultAreaId: number | string | null) => {
	if (typeof defaultAreaId === "number") {
		return defaultAreaId;
	}

	if (typeof defaultAreaId === "string" && defaultAreaId.trim() !== "") {
		const parsedValue = Number(defaultAreaId);
		return Number.isNaN(parsedValue) ? null : parsedValue;
	}

	return null;
};

export const useOperators = () => {
	return useQuery<Operator[], Error>({
		queryKey: ["operators"],
		queryFn: ({ signal }) => getOperators({}, signal),
	});
};

export const getCreateOperatorPath = () => `${operatorUrl}new`;

export const getEditOperatorPath = (operatorId: number | string) =>
	`${operatorUrl}${operatorId}`;

export const getOperatorRouteState = (pathname: string): OperatorRouteState => {
	if (!pathname.startsWith(operatorUrl)) {
		return { modal: OPERATOR_MODAL.NONE };
	}

	const [routeValue, action] = pathname
		.slice(operatorUrl.length)
		.split("/")
		.filter(Boolean);

	if (!routeValue) {
		return { modal: OPERATOR_MODAL.NONE };
	}

	if (routeValue === "new") {
		return { modal: OPERATOR_MODAL.CREATE };
	}

	if (action) {
		return { modal: OPERATOR_MODAL.NONE };
	}

	return { modal: OPERATOR_MODAL.UPDATE, operatorId: routeValue };
};

export const OperatorsPage = () => {
	const location = useLocation();
	const routeState = getOperatorRouteState(location.pathname);

	return (
		<>
			<HelmetDexteel title="Operators" />
			<TableOperators
				operatorId={routeState.operatorId}
				routeModal={routeState.modal}
			/>
		</>
	);
};
